import React, { useEffect, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import {
  getTerminal,
  updateTerminal,
  deployTerminal,
  disableTerminal,
  getTerminalLogs,
} from '../api/terminals';
import { PageLoader } from '../components/PageLoader';
import { StatusBadge } from '../components/StatusBadge';
import { CodeEditorPanel } from '../components/CodeEditorPanel';
import { ModulePicker } from '../components/ModulePicker';
import { SecretsPanel } from '../components/SecretsPanel';
import { AuthRulesPanel } from '../components/AuthRulesPanel';
import { IpRulesPanel } from '../components/IpRulesPanel';
import { LimitsPanel } from '../components/LimitsPanel';
import { DeployPanel } from '../components/DeployPanel';
import { LogsPanel } from '../components/LogsPanel';

export function TerminalEditor() {
  const { id } = useParams();
  const [terminal, setTerminal] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deploying, setDeploying] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [savedAt, setSavedAt] = useState(null);
  const [logs, setLogs] = useState([]);

  const load = useCallback(async () => {
    const data = await getTerminal(id);
    setTerminal(data);
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (terminal?.status === 'deployed') {
      getTerminalLogs(id).then(setLogs);
    }
  }, [id, terminal?.status]);

  function patch(fields) {
    setTerminal((t) => ({ ...t, ...fields }));
  }

  async function handleSave() {
    setSaving(true);
    setSaveError(null);
    try {
      const updated = await updateTerminal(id, {
        name: terminal.name,
        description: terminal.description,
        code: terminal.code,
        allowedModules: terminal.allowedModules,
        ipRules: terminal.ipRules,
        rateLimit: terminal.rateLimit,
        cors: terminal.cors,
        resourceLimits: terminal.resourceLimits,
        expiresAt: terminal.expiresAt,
      });
      setTerminal((t) => ({ ...t, ...updated }));
      setSavedAt(Date.now());
      return true;
    } catch (err) {
      setSaveError(formatError(err));
      return false;
    } finally {
      setSaving(false);
    }
  }

  async function handleDeploy() {
    setDeploying(true);
    setSaveError(null);
    try {
      const ok = await handleSave();
      if (!ok) return;
      const deployed = await deployTerminal(id);
      setTerminal((t) => ({ ...t, ...deployed }));
    } catch (err) {
      setSaveError(formatError(err));
    } finally {
      setDeploying(false);
    }
  }

  async function handleDisable() {
    const updated = await disableTerminal(id);
    setTerminal((t) => ({ ...t, ...updated }));
  }

  if (!terminal) return <PageLoader label="Loading terminal..." />;

  return (
    <div className="terminal-editor">
      <Link to="/dashboard" className="back-link">
        <ArrowLeft size={14} />
        All terminals
      </Link>

      <div className="editor-header">
        <input
          className="name-input"
          value={terminal.name}
          onChange={(e) => patch({ name: e.target.value })}
        />
        <StatusBadge status={terminal.status} />
      </div>

      <input
        className="description-input"
        placeholder="Description (optional)"
        value={terminal.description}
        onChange={(e) => patch({ description: e.target.value })}
      />

      <Section title="Handler code">
        <CodeEditorPanel code={terminal.code} onChange={(code) => patch({ code })} />
      </Section>

      <Section title="npm modules">
        <ModulePicker
          selected={terminal.allowedModules}
          onChange={(allowedModules) => patch({ allowedModules })}
        />
      </Section>

      <Section title="Secrets">
        <SecretsPanel
          terminalId={id}
          secrets={terminal.secrets}
          onChange={(secrets) => patch({ secrets })}
        />
      </Section>

      <Section title="Authentication">
        <AuthRulesPanel terminalId={id} auth={terminal.auth} onChange={(auth) => patch({ auth })} />
      </Section>

      <Section title="IP restrictions">
        <IpRulesPanel ipRules={terminal.ipRules} onChange={(ipRules) => patch({ ipRules })} />
      </Section>

      <Section title="Limits & expiry">
        <LimitsPanel
          rateLimit={terminal.rateLimit}
          resourceLimits={terminal.resourceLimits}
          expiresAt={terminal.expiresAt}
          cors={terminal.cors}
          onChange={patch}
        />
      </Section>

      {saveError && <div className="banner banner-error">{saveError}</div>}
      {savedAt && !saveError && <div className="banner banner-success">Saved.</div>}

      <Section title="Deploy">
        <div className="save-row">
          <button className="btn btn-secondary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save draft'}
          </button>
        </div>
        <DeployPanel
          terminal={terminal}
          onDeploy={handleDeploy}
          onDisable={handleDisable}
          deploying={deploying}
        />
      </Section>

      {terminal.status === 'deployed' && (
        <Section title="Recent invocations">
          <LogsPanel logs={logs} onRefresh={() => getTerminalLogs(id).then(setLogs)} />
        </Section>
      )}
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="editor-section">
      <h2>{title}</h2>
      {children}
    </section>
  );
}

function formatError(err) {
  const data = err.response?.data;
  if (!data) return err.message;
  if (Array.isArray(data.error)) return data.error.map((e) => e.message).join(', ');
  if (data.details) return `${data.error}: ${Array.isArray(data.details) ? data.details.join(', ') : data.details}`;
  return data.error || err.message;
}
